import {Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {DxDataGridComponent} from 'devextreme-angular';
import config from 'devextreme/core/config';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import * as Moment from 'moment-timezone';
import {BehaviorSubject, of, Subject} from 'rxjs';
import {debounceTime, take, takeUntil} from 'rxjs/operators';
import {ReportsService} from 'src/app/modules/shared/services/reports.service';
import {IOrderSearch} from '../../../../../shared/models/ocl';
import {FusionAuthManagementService, FusionAuthService} from '../../../../../shared/services/fusion-auth';
import {PWADetectorService, ScreenService} from '../../../../../shared/services/general';
import {FileUtils} from '../../../../../shared/utils';
import {SettingsService} from '../../../../shared/services/settings.service';
import {OrderStatus, WebOrder} from '../../models/order';
import {OrderBillingInformation} from '../../models/order-billing-information';
import {OrdersService} from '../../../../shared/services/orders.service';
import { Store } from 'devextreme/data';
import {environment} from "../../../../../../environments/environment";
import {SignatureRepresentation} from "../../../requisitions/components/signature-dialog/signature-dialog.component";
import {MultipleOrdersSignature} from "../../../../shared/models/signature";
import notify from "devextreme/ui/notify";
import {RegistrationRequest} from "../../../../../shared/models/fusion-auth";
import {CREATED} from "http-status-codes";

@Component({
  selector: 'app-laboratory-results-content',
  templateUrl: './laboratory-results-content.component.html',
  styleUrls: ['./laboratory-results-content.component.scss'],
})
export class LaboratoryResultsContentComponent implements OnInit, OnDestroy {
  @ViewChild(DxDataGridComponent, {static: true}) public dxDataGridComponent?: DxDataGridComponent;
  @Input() public filtersSubmitted: EventEmitter<any> = new EventEmitter<any>();
  @Input() public showMissingInfo = false;
  public signDialogVisible = false;
  public signedOrderSelected = false;
  public selectedItemsIds: number[] = [];
  public acknowledgeOrderSelected = false;
  public pendingOrderSelected = false;
  public missingInfoPopupVisible = false;
  public missingInfoData: OrderBillingInformation = new OrderBillingInformation();
  public ackHeaderFilter = [
    {
      text: 'Acknowledged',
      value: ['!', ['onlineAckUser', '=', null]],
    },
    {
      text: 'Not Acknowledged',
      value: ['onlineAckUser', '=', null],
    },
  ];
  public abnormalLabel: string = '';
  public isLoading = false;
  public labResultsStore: Store | [] = [];
  // @ts-ignore
  public dataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  public filters: IOrderSearch = {} as IOrderSearch;
  public emailFaxVisible = false;
  public type: string = '';
  public isPhoneOrTablet: boolean = false;
  public isPhone: boolean = false;
  public webResultSettings: any;
  public user: any;
  public updateBillToVisible = false;
  public isInterpretationEnabled: boolean = environment.aiAnalysis.enabled;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private service: OrdersService,
    private router: Router,
    private pwaDetectorService: PWADetectorService,
    public route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    public fusionAuthService: FusionAuthService,
    private reportsService: ReportsService,
    private screen: ScreenService,
    public settingsService: SettingsService,
    private fusionAuthManagement: FusionAuthManagementService,
  ) {
  }

  public getPatientRoomBed(order: WebOrder): string {
    if (order.patientRoom) {
      if (order.patientBed) {
        return `${order.patientRoom} - ${order.patientBed}`;
      } else {
        return `${order.patientRoom}`;
      }
    } else {
      return `-`;
    }
  }

  public getStatus(order: WebOrder) {
    let color;
    let status: string = OrderStatus[OrderStatus.None];
    switch (order.status) {
      case OrderStatus.Canceled:
        color = 'ocl-status-black';
        status = OrderStatus[OrderStatus.Canceled];
        break;
      case OrderStatus.Final:
        color = 'ocl-status-green';
        status = OrderStatus[OrderStatus.Final];
        break;
      case OrderStatus.Partial:
      case OrderStatus.PartiallyReleased:
      case OrderStatus.Preliminary:
        color = 'ocl-status-orange';
        status = OrderStatus[OrderStatus.Partial];
        break;
      case OrderStatus.Pending:
      case OrderStatus.Unreleased:
        color = 'ocl-status-yellow';
        status = OrderStatus[OrderStatus.Pending];
        break;
      case OrderStatus.None:
        color = 'ocl-status-black';
        status = OrderStatus[OrderStatus.None];
        break;
    }

    let alert = `
            <div fxLayout="column" fxLayoutAlign="center">
                <div style="width: 100px;height:15px!important;line-height: .45 !important" class="${color}">
                ${status.toUpperCase()}</div>
        `;

    if (order.hasAlert) {
      if (order.criticalToxResults) {
        alert += `<div style="width: 100px;height:15px!important;line-height: .45 !important" class="ocl-status-critical">
                    ${this.abnormalLabel}</div>`;
      }
      if (order.criticalMicResults || order.criticalResults) {
        alert += `<div style="width: 100px;height:15px!important;line-height: .45 !important" class="ocl-status-critical">CRITICAL</div>`;
      }
    }
    alert += `</div>`;
    return this.sanitizer.bypassSecurityTrustHtml(alert);
  }

  public ngOnInit() {
    if (this.dxDataGridComponent?.instance !== undefined) {
      this.dxDataGridComponent.instance.beginCustomLoading('');
    }

    this.responsiveValues();
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.dxDataGridComponent?.instance.clearSelection();
      this.responsiveValues();
    });

    this.settingsService.getWebResultSettings()
      .pipe(take(1))
      .subscribe(x => {
        this.webResultSettings = x;
      });

    this.settingsService.getResultSettings()
      .pipe(take(1))
      .subscribe(results => {
        this.dxDataGridComponent?.instance.endCustomLoading();
        this.abnormalLabel = results.abnormalResult;
      });

    this.waitByUser();
  }

  private waitByUser() {
    this.user = this.fusionAuthService.Profile;

    if (this.user) {
      this.filtersSubmitted
        .pipe(
          takeUntil(this.onDestroy$),
          debounceTime(1000),
        )
        .subscribe(filters => {
          this.filters = filters;
          this.setFilters(this.filters, this.user);
        });
    } else {
      of()
        .pipe(
          debounceTime(1000),
          take(1),
        )
        .subscribe(() => this.waitByUser());
    }
  }

  private responsiveValues() {
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] ||
      this.screen.sizes['screen-small'] ||
      this.screen.sizes['screen-tablet'];
    this.isPhone = this.screen.sizes['screen-x-small'];

    const offset = this.isPhone ? '-15 75' : '-25 75';
    config({
      floatingActionButtonConfig: {
        icon: 'rowfield',
        shading: true,
        direction: 'down',
        position: {
          of: 'body',
          my: 'right top',
          at: 'right top',
          offset,
        },
      },
    });
    repaintFloatingActionButton();
  }

  public get ButtonsDisabled() {
    return this.selectedItemsIds.length === 0;
  }

  public ack() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Acknowledging');
    let queryString = '';
    this.selectedItemsIds.map(x => {
      queryString += `ids=${x}&`;
    });
    this.service.acknowledgeOrder(queryString)
      .pipe(take(1))
      .subscribe(() => {
        this.selectedItemsIds = [];
        this.dxDataGridComponent?.instance.getDataSource().reload();
        this.dxDataGridComponent?.instance.clearSelection();
        this.dxDataGridComponent?.instance.endCustomLoading();
      });
  }

  public rowClick(e: any) {
    if (e.rowType === 'data' && !this.isPhoneOrTablet) {
      this.sendToPreview(e.data.id).then();
    }
  }

  public setFilters(filters: IOrderSearch, user: any) {
    if (typeof filters.startDate === 'number') {
      filters.startDate = new Date(filters.startDate);
    }

    if (typeof filters.endDate === 'number') {
      filters.endDate = new Date(filters.endDate);
    }

    const role = user.registrations[0].roles[0];
    switch (role) {
      case 'Client Admin':
      case 'Client Basic':
        const clientIds = user ? user.data.clients : this.fusionAuthService.Profile.data.clients;
        this.dataSource.next(new DataSource({
          store:
            this.service.customStore(filters, false, clientIds, undefined, undefined, this.dataSource)
        }));
        this.labResultsStore = this.dataSource.value.store();
        break;
      case 'Patient':
        const patientIds = user ? user.data.patients : this.fusionAuthService.Profile.data.patients;
        this.dataSource.next(new DataSource({
          store:
            this.service.customStore(filters, false, undefined, patientIds, undefined, this.dataSource)
        }));
        this.labResultsStore = this.dataSource.value.store();
        break;
      case 'Physician':
        const physicianIds = user ? user.data.physicians : this.fusionAuthService.Profile.data.physicians;
        this.dataSource.next(new DataSource({
          store:
            this.service.customStore(filters, false, undefined, undefined, physicianIds, this.dataSource)
        }));
        this.labResultsStore = this.dataSource.value.store();
        break;
      default:
        this.dataSource.next(new DataSource({
          store:
            this.service.customStore(filters, false, undefined, undefined, undefined, this.dataSource)
        }));
        this.labResultsStore = this.dataSource.value.store();
    }
  }

  public formatDate(date: Date) {
    const tz = Moment.tz.guess();
    return Moment.utc(date).tz(tz).format('LLLL');
  }

  public async sendToPreview(id: number) {
    if (this.pwaDetectorService.isPWA()) {
      await this.router.navigate(['/secured/results/order', {id}]);
    } else {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/secured/results/order', {id}]),
      );
      window.open(url, '_blank');
    }
  }

  public printLabResults() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateOrderResults(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public printOrderLog() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateOrderLog(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public printOrderDetails() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateOrderDetails(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public openEmailFax(type: string) {
    this.type = type;
    this.emailFaxVisible = true;
  }

  public openUpdateBillTo() {
    this.updateBillToVisible = true;
  }

  public closeUpdateBillTo() {
    this.updateBillToVisible = false;
    this.setFilters(this.filters, this.user);
  }

  public get FABDisabled() {
    return this.isPhoneOrTablet && this.selectedItemsIds ? this.selectedItemsIds.length > 0 : false;
  }

  public showColumnChooser() {
    this.dxDataGridComponent?.instance.showColumnChooser();
  }

  public onContentReady(): void {
    this.dxDataGridComponent?.instance.clearSelection();
  }

  public itemsSelected(ids: number[]) {
    this.selectedItemsIds = ids;
    if (!this.dxDataGridComponent?.instance) {
      return;
    }
    const ackFilter = this.dxDataGridComponent.instance.getSelectedRowsData().filter((r: WebOrder) => r.onlineAckUser);
    if (Array.isArray(ackFilter)) {
      this.acknowledgeOrderSelected = ackFilter.length !== 0;
    }

    const pendingOrderFiler = this.dxDataGridComponent.instance.getSelectedRowsData()
      .filter((r: WebOrder) => r.status === OrderStatus.Pending || r.status === OrderStatus.Unreleased);
    if (Array.isArray(pendingOrderFiler)) {
      this.pendingOrderSelected = pendingOrderFiler.length !== 0;
    }
    this.signedOrderSelected =
      this.dxDataGridComponent.instance.getSelectedRowsData().filter(r => r.hasSignature).length !== 0;
  }

  public testResult(testId: number, order: any) {
    const test = order.orderResults.find((x: any) => x.testId === testId);
    if (test) {
      if (test.reportAs) {
        return test.flag === null || test.flag === '' ? test.reportAs : `${test.reportAs} (${test.flag})`;
      } else {
        return test.flag === null || test.flag === '' ? test.results : `${test.results} (${test.flag})`;
      }

    } else {
      return 'NOT ORDERED';
    }
  }

  public signOrders() {
    this.signDialogVisible = true;
  }

  public signDialogClosed(signatureDialogResponse: SignatureRepresentation) {
    if (signatureDialogResponse) {
      const signature = new MultipleOrdersSignature();
      signature.image = signatureDialogResponse.image;
      signature.username = signatureDialogResponse.username;
      signature.ordersIds = this.selectedItemsIds;

      this.dxDataGridComponent?.instance.beginCustomLoading(
        this.selectedItemsIds.length === 1 ? 'Signing order' : 'Signing orders',
      );
      const user = this.fusionAuthService.Profile;
      user.data = {
        signImage: signatureDialogResponse.image,
        clients: user.data !== undefined ? user.data.clients : [],
        patients: user.data !== undefined ? user.data.patients : [],
        physicians: user.data !== undefined ? user.data.physicians : [],
      };
      const request: RegistrationRequest = {
        user,
        registration: {
          applicationId: environment.fusionAuth.applicationId,
          roles: [user.role],
          username: user.username,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      this.fusionAuthManagement
        .updateUser(user.id, request)
        .pipe(take(1))
        .subscribe(response => {
          if (response.status === CREATED) {
            this.service.signOrders(signature)
              .pipe(take(1))
              .subscribe(() => {
                this.fusionAuthService.SignatureImage = signatureDialogResponse.image;
                this.dxDataGridComponent?.instance.endCustomLoading();
                notify(this.selectedItemsIds.length === 1 ? 'Order Signed!' : 'Orders Signed!', 'success');
                this.dxDataGridComponent?.instance.refresh();
              });
          }
        });

      /*this.service.signOrders(signature)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.dxDataGridComponent?.instance.endCustomLoading();
          notify(this.selectedItemsIds.length === 1 ? 'Order Signed!' : 'Orders Signed!', 'success');
          this.dxDataGridComponent?.instance.refresh();
        });*/
    }

    this.signDialogVisible = false;
  }

  public closeMissingInfo(canceled: boolean) {
    this.missingInfoPopupVisible = false;
    if (!canceled) {
      this.setFilters(this.filters, this.user);
    }
  }

  public openMissingInfo({data}: { data: any }) {
    this.isLoading = true;
    this.service.getOrderBillingInfo(data.id)
      .pipe(take(1))
      .subscribe(res => {
        this.missingInfoData = res;
        this.missingInfoData.orderCode = data.codeValue;
        this.missingInfoPopupVisible = true;
        this.isLoading = false;
      });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
